import { z } from 'zod';
import { ASSET_KINDS } from './models';

export const ASSET_STATUSES = [
  'uploading',
  'processing',
  'available',
  'authorization_required',
  'failed',
  'deleting',
  'deleted',
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const TRUST_CLASSES = ['normal', 'virtual_avatar', 'real_person'] as const;
export type TrustClass = (typeof TRUST_CLASSES)[number];

export const SINGLE_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;
export const MULTIPART_PART_SIZE = 16 * 1024 * 1024;

export const CreateUploadSchema = z.object({
  filename: z.string().min(1).max(255),
  content_type: z.string().min(1).max(127),
  size_bytes: z.number().int().positive().max(2 * 1024 * 1024 * 1024),
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
});
export type CreateUpload = z.infer<typeof CreateUploadSchema>;

export const UploadSessionSchema = z.object({
  id: z.string(),
  method: z.enum(['single', 'multipart']),
  upload_url: z.string().nullable(),
  headers: z.record(z.string(), z.string()),
  part_size: z.number().nullable(),
  part_count: z.number().nullable(),
  expires_at: z.string(),
});
export type UploadSession = z.infer<typeof UploadSessionSchema>;

export const UploadPartsRequestSchema = z.object({
  part_numbers: z.array(z.number().int().min(1).max(10000)).min(1).max(100),
});
export const CompleteUploadSchema = z.object({
  parts: z.array(z.object({ part_number: z.number().int(), etag: z.string() })).optional(),
});

export const CreateAssetSchema = z
  .object({
    upload_id: z.string().optional(),
    source_url: z.url({ protocol: /^https?$/ }).max(2048).optional(),
    name: z.string().min(1).max(200),
    group_id: z.string().nullable().optional(),
    trust_class: z.enum(TRUST_CLASSES).default('normal'),
  })
  .refine((v) => Boolean(v.upload_id) !== Boolean(v.source_url), {
    message: 'upload_id 与 source_url 必须且只能提供一个',
    path: ['upload_id'],
  });
export type CreateAsset = z.infer<typeof CreateAssetSchema>;

export const UpdateAssetSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  group_id: z.string().nullable().optional(),
});

export const AssetObjectSchema = z.object({
  id: z.string(),
  object: z.literal('asset'),
  name: z.string(),
  kind: z.enum(ASSET_KINDS).nullable(),
  trust_class: z.enum(TRUST_CLASSES),
  status: z.enum(ASSET_STATUSES),
  status_reason: z.string().nullable(),
  group_id: z.string().nullable(),
  version: z.number().nullable(),
  mime: z.string().nullable(),
  size_bytes: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  duration_ms: z.number().nullable(),
  sha256: z.string().nullable(),
  channels_ready: z.number(),
  created_at: z.string(),
});
export type AssetObject = z.infer<typeof AssetObjectSchema>;

export const AssetGroupSchema = z.object({
  id: z.string(),
  object: z.literal('asset_group'),
  name: z.string(),
  asset_count: z.number(),
  created_at: z.string(),
});
export type AssetGroup = z.infer<typeof AssetGroupSchema>;

export const UpsertAssetGroupSchema = z.object({ name: z.string().min(1).max(64) });

export const StorageUsageSchema = z.object({ used_bytes: z.number(), quota_bytes: z.number() });
export type StorageUsage = z.infer<typeof StorageUsageSchema>;

/** 真人认证（H5 活体）：一份真人素材在某个模型上的一次认证 */
export const PORTRAIT_AUTH_STATUSES = ['pending', 'verified', 'failed', 'expired'] as const;
export const PortraitAuthorizationSchema = z.object({
  id: z.string(),
  object: z.literal('portrait_authorization'),
  asset_id: z.string(),
  model: z.string(),
  status: z.enum(PORTRAIT_AUTH_STATUSES),
  /** 待认证时给出，让本人用手机打开完成刷脸；一次性，30 分钟内有效 */
  h5_link: z.string().nullable(),
  expires_at: z.string(),
  verified_at: z.string().nullable(),
  error: z.string().nullable(),
  created_at: z.string(),
});
export type PortraitAuthorization = z.infer<typeof PortraitAuthorizationSchema>;

export const CreatePortraitAuthorizationSchema = z.object({ model: z.string().min(1).max(64) });
export type CreatePortraitAuthorization = z.infer<typeof CreatePortraitAuthorizationSchema>;
